import { useState, useEffect } from 'react';
import { parseDate } from 'utils/format';
import httpRequest from 'utils/httpRequest';

const instagramBasicApiUrl = (query, token) => {
  return `https://graph.instagram.com/${query}&access_token=${token}`;
};

const instagramDataFetch = async url => {
  try {
    const response = await httpRequest(url, {
      method: 'GET',
    });
    if (!response.ok) {
      return { error: response.status };
    }

    const json = await response.json();
    return { data: json };
  } catch (e) {
    return { error: 'Failed to fetch' };
  }
};

const getMediaYear = timestamp => {
  return parseDate(timestamp, 'YYYY-MM-DDTHH:mm:ssZ').getFullYear();
};

/**
 * useIGFetch
 *
 * @param {string} query
 * @param {string} token
 * @param {boolean} asArray
 * @param {number} year when 0, collect all photos, when is greater than zero - choose yurrent year
 */
const useIGFetch = (query = '', token = undefined, asArray = false, year = 0) => {
  const MAX_REQUEST_LIMIT = 40;

  const [dataFetched, setDataFetched] = useState();
  const [isLoading, setLoading] = useState(true);
  const [isError, setError] = useState(false);

  const arrayDataFetch = async (igQuery, igToken, onSuccess) => {
    let currentPage = 0;
    let nextUrl = instagramBasicApiUrl(igQuery, igToken);
    let result = [];

    while (currentPage < MAX_REQUEST_LIMIT) {
      const { data, error } = await instagramDataFetch(nextUrl);
      if (error) {
        setError(true);
        setLoading(false);
        return;
      }
      result = [...result, ...data.data];

      if (!data.paging.next) {
        break;
      }

      if (year > 0) {
        const lastMediaYear = getMediaYear(result[result.length - 1].timestamp);

        if (lastMediaYear < year) {
          break;
        }
      }

      nextUrl = data.paging.next;
      currentPage += 1;
    }

    const carouselsUrls = result
      .filter(media => media.media_type === 'CAROUSEL_ALBUM')
      .map(media =>
        instagramBasicApiUrl(
          `${media.id}/children?fields=id,media_url,media_type,timestamp`,
          igToken,
        ),
      );

    for (let index = 0; index < carouselsUrls.length; index += 1) {
      const { data, error } = await instagramDataFetch(carouselsUrls[index]);
      if (error) {
        setError(true);
        setLoading(false);
        return;
      }
      result = [...result, ...data.data];
    }

    result = result.filter(media => media.media_type === 'IMAGE');

    if (year > 0) {
      result = result.filter(media => getMediaYear(media.timestamp) === year);
    }

    onSuccess(result);
  };

  const objectDataFetch = async (igQuery, iqToken, onSuccess) => {
    const url = instagramBasicApiUrl(igQuery, iqToken);

    const { data, error } = await instagramDataFetch(url);
    if (error) {
      setError(true);
      setLoading(false);
      return;
    }

    onSuccess(data);
  };

  useEffect(() => {
    if (!token) return;
    if (!query) return;

    if (asArray) {
      arrayDataFetch(query, token, data => {
        setDataFetched(data);
        setLoading(false);
      });
    } else {
      objectDataFetch(query, token, data => {
        setDataFetched(data);
        setLoading(false);
      });
    }
  }, [query, token, asArray]);

  return [isLoading, isError, dataFetched];
};

export default useIGFetch;
